import React, { useContext, useEffect, useState, useRef } from "react";
import { cn, toPlainText } from "../../lib/helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useStaticQuery, graphql } from "gatsby";
import {
  faArrowLeft, faArrowRight, faPersonMilitaryPointing, faTrophy
} from "@fortawesome/free-solid-svg-icons";
import useAccountCR from "../../lib/user";
import { ScoutCopilot } from "../scout-copilot";
import { SiteContext } from "../global/site-context";
import { ZendeskWebform } from "./zendesk-webform";

// "Website Support" workflow
const DEFAULT_COPILOT = "copilot_cmb1fpu9n00010fs6dk53mpjr";
const DEFAULT_WORKFLOW = "wf_cmb12xa1s00010ds6n9lc67ru";

// Support bot is default via query
const agentQuery = graphql`
  query {
    defaultAgent: sanityAgent(_id: { eq: "7cf4bfbb-c006-4793-978d-e443ffdef59a" }) {
      ...Agent
    }
    supportAgent: sanityAgent(_id: { eq: "7cf4bfbb-c006-4793-978d-e443ffdef59a" }) {
      ...Agent
    }
  }
`;

const TICKET_PATH = "/ask?ref=chatbot-achievement";

export const Chatbot = ({
  section, copilotID, workflowID, agentPage, agent,
  minimal, showSupport, noHistory, firstMessage, className, style,
  textScroll, whiteBg, skipAutoFocus, stickyForm
}) => {
  const { defaultAgent, supportAgent } = useStaticQuery(agentQuery) || {};
  const crAccount = useAccountCR();
  const [goal, setGoal] = useState(0);
  const [mainGoal, setMainGoal] = useState("");
  const site = useContext(SiteContext);
  const [messages, setMessages] = useState([]);
  const [revealSupportForm, setRevealSupportForm] = useState(false);
  const supportZone = site?.doc?.slug?.current === "help";

  const isRoomie = !!site.metrics.analytics.storage.getItem("roomie");
  const theAgent = (agent?.role && agent?.tasks) ? agent
    : supportZone ? supportAgent : defaultAgent;

  const loadChatHistory = (agentId) => {
    if (!agentId) return [];
    try {
      const stored = localStorage.getItem(`chat-history-${agentId}`);
      if (!stored) return [];
      // JSON chat history in localStorage is double-stringified
      return JSON.parse(JSON.parse(stored)).map(msg => ({
        ...msg,
        datetime: new Date(msg.datetime)
      }));
    } catch (error) {
      console.warn('Failed to load chat history:', error);
      return [];
    }
  };

  const thePrompt = {
    role: `${defaultAgent?.role} ${agent?.role ? `| ${agent.role}` : ""}`,
    tasks: theAgent?.tasks,
    interject: toPlainText(theAgent?._rawInterject),
    answers: toPlainText(theAgent?._rawAnswers),
    questions: theAgent?.questions && theAgent?.questions.length > 0
      ? theAgent?.questions
      : agent?.questions || [],
    steps:
      theAgent?.steps?.length > 0
        ? theAgent?.steps.map(s => ({
          goal: s.goal,
          smarts: s.smarts,
          response: s._rawResponse,
          achievement: s.achievement
        }))
        : [],
  };

  useEffect(() => {
    if (!noHistory) {
      setMessages(loadChatHistory(theAgent._id));
    }
    const storedGoal = site.metrics.analytics.storage.getItem(`chat-goal-${theAgent?._id || "default"}`);
    if (storedGoal) setMainGoal(storedGoal);
  }, [noHistory]);

  useEffect(() => {
    if (!mainGoal) return;
    site.metrics.analytics.storage.setItem(`chat-goal-${theAgent?._id || "default"}`, mainGoal);
  }, [mainGoal]);

  useEffect(() => {
    if (thePrompt?.steps?.[goal]?.achievement?.url === TICKET_PATH) {
      setRevealSupportForm(true);
    }
  }, [goal]);

  useEffect(() => {
    if (crAccount) {
      console.debug('User data loaded', crAccount);
    }
  }, [crAccount]);

  return (
    <div className={cn(minimal ? "w-full" : "pb-24 lg:pb-36 z-50", className)} style={style}>
      <div
        className="flex justify-center"
        style={minimal ? null : { minHeight: "50vh" }}
      >
        <ScoutCopilot
          onResult={(result) => {
            // console.log("Result from Scout Copilot:", result);
          }}
          className="w-full"
          // @todo remove anchor as possible copilotID
          copilotID={copilotID || theAgent?.copilot_id || DEFAULT_COPILOT}
          workflowID={workflowID || theAgent?.workflow_id || DEFAULT_WORKFLOW}
          goal={goal}
          setGoal={setGoal}
          setMainGoal={setMainGoal}
          prompt={thePrompt}
          agentPage={agentPage}
          messages={messages}
          setMessages={setMessages}
          userEmail={crAccount?.profile?.email}
          minimal={minimal}
          agent={theAgent}
          showSupport={showSupport}
          firstMessage={firstMessage}
          textScroll={textScroll}
          whiteBg={whiteBg}
          skipAutoFocus={skipAutoFocus}
          stickyForm={stickyForm}
        />
      </div>
      <div className="flex flex-col items-center">
        {mainGoal && !minimal && (
          <p className="mb-3 bg-light-60 hover:bg-light-80 rounded-lg px-4 py-2 text-dark-60 hover:text-black opacity-80 hover:opacity-100 transition-all duration-300">
            <span className="text-dark-30 font-semibold">
              <FontAwesomeIcon icon={faTrophy} className="mr-1 max-h-6" />
              Goal:
            </span> {mainGoal}
          </p>
        )}
        {isRoomie && thePrompt?.steps?.length > 0 && (
          <>
            <div className="mt-6 bg-light-60 hover:bg-white px-4 py-1.5 rounded-xl border font-medium border-dark-40 text-dark-60 flex gap-4 justify-between items-center w-full max-w-3/4 opacity-50 hover:opacity-100 transition-opacity duration-300">
              <FontAwesomeIcon
                icon={faPersonMilitaryPointing}
                className="text-dark-30 max-h-8"
              />
              <p className="text-left flex-grow">
                {thePrompt.steps?.[goal]?.goal}
              </p>
              <div className="flex">
                <button
                  onClick={() => {
                    if (goal > 0) {
                      setGoal(goal - 1);
                    }
                  }}
                  className={cn(
                    "p-2 outline-none focus:outline-none flex gap-2 items-center text-dark-30 hover:text-link transition-opacity duration-300",
                    goal > 0 ? "opacity-100" : "opacity-0 cursor-not-allowed"
                  )}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="max-h-8" />
                </button>
                <button
                  onClick={() => {
                    if (goal < thePrompt.steps?.length - 1) {
                      setGoal(goal + 1);
                    }
                  }}
                  className={cn(
                    "p-2 outline-none focus:outline-none flex gap-2 items-center text-dark-30",
                    goal < thePrompt.steps?.length - 1 ? "hover:text-link" : "cursor-not-allowed"
                  )}
                >
                  ({goal + 1}/{thePrompt.steps?.length})
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="max-h-8"
                  />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {revealSupportForm && (
        <ZendeskWebform
          section={section}
          prefill={{
            subject: mainGoal,
            email: crAccount?.profile?.email || "",
            description: messages.map(m =>
              `${m.who === "user" ? "YOU" : "BOT"}: ${m.text}`
            ).join("\n\n"),
          }}
        />
      )}
    </div>
  );
};
